import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { noop } from 'lodash';
import styled from 'styled-components';
import url from 'url';
import { grey25, navy } from '@zocdoc/branding/colors';
import { Arrow } from '@zocdoc/components/src/icons';
import { PaddedContainer } from '@zocdoc/components/src/Layout';
import { FontFamily } from '@zocdoc/components/src/typography';
import { filterToValidAttributes } from '@zocdoc/dom-property';

import { CONTROL_TYPES, LOCALIZED_STRINGS } from './constants';
import { formQueryObject } from '../../../actions/updateQueryParameters';

export const DATA_ATTR_WRAPPER = 'search-results-pagination';
export const DATA_ATTR_CONTROL = 'search-results-pagination-control';

/**
 * lifted from https://github.com/zocdoc-patient/component-sdk/blob/master/src/link/Link.js
 * need to adjust the component sdk implementation before we can use that instead
 */
function determineTarget(event, target = '_self') {
    const windowsCtrlKey = event.ctrlKey;
    const macOsCmdKey = event.metaKey;
    const mouseMiddleButton = event.button === 1;

    if (windowsCtrlKey || macOsCmdKey || mouseMiddleButton) {
        return '_blank';
    } else {
        return target;
    }
}

class SearchPagination extends React.PureComponent {
    static propTypes = {
        // ours
        maxNumberOfNumberedControls: PropTypes.number,
        page: PropTypes.number,
        pageCount: PropTypes.number,
        parameters: PropTypes.object,
        trackPaginationNumberClick: PropTypes.func,
        updateParameters: PropTypes.func,

        // injected stuff
        intl: PropTypes.object,
        location: PropTypes.object,
    };

    static defaultProps = {
        location: {},
        maxNumberOfNumberedControls: 10,
        page: 0,
        pageCount: 1,
        trackPaginationNumberClick: noop,
    };

    render() {
        return (
            <PaddedContainer
                component="nav"
                data-test={DATA_ATTR_WRAPPER}
                {...filterToValidAttributes(this.props)}
            >
                {this.generateControls(this.props.page, this.props.pageCount)}
            </PaddedContainer>
        );
    }

    generateControl(targetPageIndex, content = targetPageIndex, title, key) {
        const controlType = content === targetPageIndex ? CONTROL_TYPES.NUMBER : CONTROL_TYPES.ARROW;

        return (
            <Link
                key={key}
                data-control-type={controlType}
                data-pagination-control-target-index={targetPageIndex - 1}
                data-test={DATA_ATTR_CONTROL}
                disabled={this.props.page === targetPageIndex - 1}
                href={url.format({
                    pathname: this.props.location.pathname,
                    query: formQueryObject({
                        ...this.props.parameters,
                        pageOffset: targetPageIndex - 1,
                    }),
                })}
                onClick={this.handleClick}
                rel="nofollow"
                title={title}
            >
                {content}
            </Link>
        );
    }

    generateControls(currentZeroIndexedPage, totalPages) {
        const currentPage = currentZeroIndexedPage + 1;

        let startPage;

        if (currentPage + this.props.maxNumberOfNumberedControls > totalPages) {
            startPage = Math.max(totalPages - (this.props.maxNumberOfNumberedControls - 1), 1);
        } else {
            startPage = currentPage;
        }

        const endPage = Math.min(startPage + (this.props.maxNumberOfNumberedControls - 1), totalPages);
        const controls = [];

        if (currentPage !== 1) {
            controls.push(
                this.generateControl(
                    currentPage - 1,
                    <Arrow
                        data-test="search-pagination-previous"
                        direction="left"
                        height={10}
                    />,
                    this.props.intl.formatMessage(LOCALIZED_STRINGS.PreviousToggleHoverText, {
                        index: currentPage - 1,
                    }),
                    'prev'
                )
            );
        }

        for (let i = startPage; i <= endPage; i++) {
            controls.push(
                this.generateControl(
                    i,
                    undefined,
                    this.props.intl.formatMessage(LOCALIZED_STRINGS.NumericalToggleHoverText, {
                        index: i,
                    }),
                    i
                )
            );
        }

        if (currentPage < totalPages) {
            controls.push(
                this.generateControl(
                    currentPage + 1,
                    <Arrow
                        data-test="search-pagination-next"
                        direction="right"
                        height={10}
                    />,
                    this.props.intl.formatMessage(LOCALIZED_STRINGS.NextToggleHoverText, {
                        index: currentPage + 1,
                    }),
                    'next'
                )
            );
        }

        return controls;
    }

    performPaginationClickTracking = (event, index) => {
        const controlType = event.currentTarget.getAttribute('data-control-type');
        if (controlType === CONTROL_TYPES.NUMBER) {
            this.props.trackPaginationNumberClick({ pageNumber: index + 1 });
        }
    }

    handleClick = async event => {
        event.preventDefault();

        if (event.currentTarget.getAttribute('disabled') !== null) { return; }

        const index = parseInt(event.currentTarget.getAttribute('data-pagination-control-target-index'), 10);
        const target = determineTarget(event, event.currentTarget.target);

        this.performPaginationClickTracking(event, index);

        if (target === '_blank') {
            window.open(event.currentTarget.href, target);
        } else {
            window.scrollTo(0, 0);
            this.props.updateParameters({ pageOffset: index });
        }
    }
}

const Link = styled.a`
    align-items: center;
    justify-content: center;
    background: ${props => props.disabled ? grey25 : 'transparent'};
    color: ${navy};
    display: inline-flex;
    font: 13px/0 ${FontFamily.SharpSansSemibold};
    pointer-events: ${p => p.disabled ? 'none' : 'auto'};
    position: relative;
    height: 32px;
    width: 32px;

    &:hover {
        background: ${grey25};
        text-decoration: none;
    }
`;

export default injectIntl(withRouter(SearchPagination));
